import type { Task } from 'fhir/r4';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import SemanticDatepicker from 'react-semantic-ui-datepickers';
import { Button, Form, Message } from 'semantic-ui-react';

type ConsultCancelDueDateWarningProps = {
  /**
   * Confirms with the member cancel/no-show date (`YYYY-MM-DD`) so due date and
   * status can be saved together in one FHIR write.
   */
  onConfirm: (memberEventDate: string) => void;
  onDismiss: () => void;
  open: boolean;
  /** Target status triggering the reminder (`cancelled` or `failed`). */
  targetStatus?: Task['status'] | null;
};

/**
 * Collects the member cancel/no-show date before marking a consult cancelled or failed,
 * so Care Betty can set due date + status in a single save (task detail + bulk header).
 */
export const ConsultCancelDueDateWarning: React.FC<ConsultCancelDueDateWarningProps> = ({
  open,
  onDismiss,
  onConfirm,
  targetStatus,
}) => {
  const [memberEventDate, setMemberEventDate] = useState<Date>(moment().startOf('day').toDate());

  useEffect(() => {
    if (open) {
      setMemberEventDate(moment().startOf('day').toDate());
    }
  }, [open]);

  if (!open) return null;

  const statusLabel = targetStatus === 'failed' ? 'failed' : 'cancelled';
  const canConfirm = Boolean(memberEventDate) && moment(memberEventDate).isValid();

  return (
    <Message
      warning
      onDismiss={onDismiss}
      header={`Consult task: set due date and mark ${statusLabel}`}
      content={
        <div>
          <p>
            Choose the due date that matches when the member cancelled or no-showed (today is fine when that is the
            correct date). Due date and status are saved together so only the failed/cancelled handler runs.
          </p>
          <Form style={{ marginBottom: '0.75rem' }}>
            <Form.Field
              required
              control={SemanticDatepicker}
              label="Due date (member cancel / no-show date)"
              placeholder="Select date"
              datePickerOnly
              clearable={false}
              value={memberEventDate}
              onChange={(_e: unknown, data: { value?: Date | Date[] | null }) => {
                const next = Array.isArray(data?.value) ? data.value[0] : data?.value;
                if (next) {
                  setMemberEventDate(next);
                }
              }}
            />
          </Form>
          <Button size="small" onClick={onDismiss}>
            Go back
          </Button>{' '}
          <Button
            size="small"
            primary
            disabled={!canConfirm}
            onClick={() => {
              if (!canConfirm) return;
              onConfirm(moment(memberEventDate).format('YYYY-MM-DD'));
            }}
          >
            Save due date and mark {statusLabel}
          </Button>
        </div>
      }
    />
  );
};
